import { formatDateTime, formatMoney, formatTime } from '@comforthub/design-system'
import { formatOrderNr } from '../orderNr'
import {
  DEFAULT_WORK_SLIP_FIELDS,
  suggestedMark,
  workSlipGoodsTotal,
  workSlipItemLabel,
  workSlipNoteText,
  workSlipOptionText,
  workSlipPromoLines,
  type WorkSlipOrder,
  type WorkSlipOrderItem,
  type WorkSlipSettings,
} from '../orderWorkSlip'

/**
 * What goes on an 80 mm work slip, line by line, before it is drawn
 * (renderSlip draws it, escpos turns the picture into printer bytes).
 *
 * Pure, so the content is tested without a canvas. It honours the same
 * Company → Work slip toggles as the A4 slip.
 */

export type SlipSize = 'sm' | 'md' | 'lg' | 'xl'

export type SlipLine =
  | { kind: 'text'; text: string; size?: SlipSize; bold?: boolean; align?: 'left' | 'center'; indent?: number }
  /** Text on the left, text on the right (an item and its price). */
  | { kind: 'row'; left: string; right: string; size?: SlipSize; bold?: boolean }
  /** A label over its value: "DUE" then "19 Sep 2026, 14:30". */
  | { kind: 'label'; text: string }
  | { kind: 'rule' }
  | { kind: 'gap' }

/** The order fields the receipt reads. The detail OrderDto has all of them. */
export type ReceiptOrderItem = WorkSlipOrderItem

export interface ReceiptOrder extends Omit<WorkSlipOrder, 'items'> {
  placedAt?: string | null
  customerPhone?: string | null
  addressText?: string | null
  calendarBound?: boolean | null
  appointment?: { start?: string | null; end?: string | null } | null
  total?: number | null
  amount?: number | null
  paymentStatus?: string | null
  items?: ReceiptOrderItem[] | null
}

function fulfillmentLabel(order: ReceiptOrder): string | null {
  if (order.calendarBound) return 'Visit'
  const mode = (order.fulfillment || '').trim().toLowerCase()
  if (!mode) return null
  if (mode === 'pickup') return 'Pickup'
  if (mode === 'onsite') return 'On site'
  return 'Delivery'
}

/**
 * "13 Oct 2026, 14:00–15:00": always dated, never "Today", because a slip is
 * often read on another day than it was printed.
 */
function datedWindow(start: string, end?: string | null): string {
  const from = formatDateTime(start)
  if (!end || end === start) return from
  // formatDateTime is "19 Sep 2026, 14:30" on the Tallinn clock: compare its date part.
  const day = (value: string) => formatDateTime(value).split(', ')[0]
  const sameDay = day(start) === day(end)
  return sameDay ? `${from}–${formatTime(end)}` : `${from} – ${formatDateTime(end)}`
}

/** Equal once trimmed, ignoring case; never equal when either is blank. */
function sameText(a?: string | null, b?: string | null): boolean {
  const x = a?.trim().toLowerCase()
  return !!x && x === b?.trim().toLowerCase()
}

export function buildSlipLines(
  order: ReceiptOrder,
  settings: WorkSlipSettings,
  printedAt: Date = new Date(),
): SlipLine[] {
  const fields = { ...DEFAULT_WORK_SLIP_FIELDS, ...(settings.fields ?? {}) }
  const lines: SlipLine[] = []
  const header = settings.headerText?.trim() || 'Work slip'

  lines.push({ kind: 'text', text: header, size: 'lg', bold: true, align: 'center' })
  lines.push({
    kind: 'text',
    text: order.orderNr || formatOrderNr(order.id),
    size: 'xl',
    bold: true,
    align: 'center',
  })
  if (order.placedAt) {
    lines.push({ kind: 'text', text: `Placed ${formatDateTime(order.placedAt)}`, align: 'center' })
  }
  // A store named like the header text ("Unusual projects OÜ" twice) prints once,
  // as on the cloud slip.
  if (order.storeName && !sameText(order.storeName, settings.headerText)) {
    lines.push({ kind: 'text', text: order.storeName, align: 'center' })
  }
  lines.push({ kind: 'rule' })

  if (fields.showReadyBy) {
    // The pickup time, else the old prep target; never readyBy (the placed
    // time on older orders). Always dated: a slip is often read the next day.
    const prepAt = order.pickupAt || order.targets?.prep?.endAt || order.targets?.prep?.startAt
    const delivery = order.targets?.delivery
    const visit = order.calendarBound ? order.appointment : null
    if (prepAt || delivery?.startAt || visit?.start) {
      lines.push({ kind: 'label', text: 'Due' })
      if (prepAt) {
        lines.push({ kind: 'text', text: `Ready by ${formatDateTime(prepAt)}${suggestedMark(order)}`, size: 'lg', bold: true })
      }
      if (delivery?.startAt) {
        lines.push({ kind: 'text', text: `Deliver ${datedWindow(delivery.startAt, delivery.endAt)}` })
      }
      if (visit?.start) {
        lines.push({ kind: 'text', text: `Visit ${datedWindow(visit.start, visit.end)}` })
      }
      lines.push({ kind: 'gap' })
    }
  }

  const mode = fulfillmentLabel(order)
  if (fields.showFulfillment && mode) {
    lines.push({ kind: 'label', text: 'Fulfillment' })
    lines.push({ kind: 'text', text: mode, bold: true })
    if (mode === 'Delivery' && order.addressText) lines.push({ kind: 'text', text: order.addressText })
    lines.push({ kind: 'gap' })
  }

  if (fields.showCustomer && (order.customerName || order.customerPhone)) {
    lines.push({ kind: 'label', text: 'Client' })
    if (order.customerName) lines.push({ kind: 'text', text: order.customerName })
    if (order.customerPhone) lines.push({ kind: 'text', text: order.customerPhone })
    lines.push({ kind: 'gap' })
  }

  if (fields.showRecipient && (order.recipientName || order.recipientPhone)) {
    lines.push({ kind: 'label', text: 'Recipient' })
    if (order.recipientName) lines.push({ kind: 'text', text: order.recipientName })
    if (order.recipientPhone) lines.push({ kind: 'text', text: order.recipientPhone })
    lines.push({ kind: 'gap' })
  }

  if (fields.showGiftMessage && order.giftMessage?.trim()) {
    lines.push({ kind: 'label', text: 'Gift message' })
    lines.push({ kind: 'text', text: order.giftMessage.trim(), size: 'lg' })
    lines.push({ kind: 'gap' })
  }

  if (fields.showNotes && order.notes?.trim()) {
    lines.push({ kind: 'label', text: 'Notes' })
    lines.push({ kind: 'text', text: order.notes.trim() })
    lines.push({ kind: 'gap' })
  }

  const items = order.items ?? []
  if (fields.showItems && items.length > 0) {
    lines.push({ kind: 'rule' })
    for (const item of items) {
      const qty = item.quantity != null && item.quantity !== 1 ? `${item.quantity} × ` : ''
      const label = `${qty}${workSlipItemLabel(item)}`
      if (fields.showPrices && item.lineTotal != null) {
        lines.push({ kind: 'row', left: label, right: formatMoney(item.lineTotal), bold: true })
      } else {
        lines.push({ kind: 'text', text: label, bold: true })
      }
      for (const option of item.options ?? []) {
        const text = workSlipOptionText(option)
        if (text) lines.push({ kind: 'text', text: `+ ${text}`, indent: 1 })
      }
      // The client's comment, under its line's options; it wraps, it is never cut.
      const note = workSlipNoteText(item)
      if (note) lines.push({ kind: 'text', text: note, indent: 1 })
    }
    // Goods and services only: never order.total, which includes the delivery fee.
    const total = workSlipGoodsTotal(order)
    if (fields.showPrices && total != null) {
      lines.push({ kind: 'rule' })
      // The store pays for its promo codes: the goods before the discount and the discount, above
      // the Total it is paid for. As on the cloud slip.
      const promo = workSlipPromoLines(order)
      if (promo) {
        lines.push({ kind: 'row', left: 'Goods', right: formatMoney(promo.goods) })
        lines.push({ kind: 'row', left: promo.label, right: formatMoney(-promo.discount) })
      }
      lines.push({ kind: 'row', left: 'Total', right: formatMoney(total), bold: true })
    }
  }

  lines.push({ kind: 'rule' })
  lines.push({ kind: 'text', text: `Printed ${formatDateTime(printedAt.toISOString())}`, size: 'sm', align: 'center' })
  return lines
}
